import { useEffect, useState, type CSSProperties } from 'react'
import { getDebugServerInfo, type DebugServerInfo } from '@/lib/tauri'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip'

/**
 * Status dot for the always-on debug HTTP server (task 2901).
 *
 * Polls `http://127.0.0.1:<port>/debug/health` every 5 s and shows:
 *   - green when the server is responsive,
 *   - red when the fetch fails or returns non-2xx,
 *   - muted grey while waiting for the first response.
 *
 * The dot's existence is the operator's reminder that the debug API is
 * live; the tooltip exposes the bound port and build mode so an external
 * driver (Claude, scripts) can target the right URL without guessing.
 *
 * Renders nothing if the Rust `get_debug_server_info` invoke fails
 * (non-Tauri context, e.g., browser tests).
 *
 * Mount inside an existing `TooltipProvider` ancestor — App.tsx provides one
 * at the route-tree root.
 */

type HealthState = 'unknown' | 'healthy' | 'unhealthy'

const POLL_INTERVAL_MS = 5_000

export function DebugServerIndicator() {
  const [info, setInfo] = useState<DebugServerInfo | null>(null)
  const [health, setHealth] = useState<HealthState>('unknown')

  // Resolve bound port/mode once on mount. Failure (non-Tauri context, or the
  // command is missing) hides the indicator entirely.
  useEffect(() => {
    let cancelled = false
    getDebugServerInfo()
      .then((next) => {
        if (!cancelled) setInfo(next)
      })
      .catch(() => {
        if (!cancelled) setInfo(null)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Poll /debug/health on a fixed interval once we know the port.
  useEffect(() => {
    if (!info) return
    let cancelled = false

    const ping = async () => {
      try {
        const res = await fetch(`http://127.0.0.1:${info.port}/debug/health`)
        if (cancelled) return
        setHealth(res.ok ? 'healthy' : 'unhealthy')
      } catch {
        if (cancelled) return
        setHealth('unhealthy')
      }
    }

    void ping()
    const id = window.setInterval(() => {
      void ping()
    }, POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [info])

  if (!info) return null

  const dotStyle: CSSProperties = {
    ...styles.dot,
    background: colorFor(health),
    boxShadow: health === 'healthy' ? '0 0 6px var(--success)' : 'none',
  }

  const tooltipText = `Debug API: 127.0.0.1:${info.port} (${info.mode})`

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          style={dotStyle}
          role="status"
          aria-label={tooltipText}
          aria-live="polite"
        />
      </TooltipTrigger>
      <TooltipContent>{tooltipText}</TooltipContent>
    </Tooltip>
  )
}

function colorFor(state: HealthState): string {
  switch (state) {
    case 'healthy':
      return 'var(--success)'
    case 'unhealthy':
      // No --danger token in the design system; existing red usage is the
      // raw hex below (see Header.tsx env badge red equivalents).
      return '#e35a5a'
    case 'unknown':
    default:
      return 'var(--text-muted)'
  }
}

const styles: Record<string, CSSProperties> = {
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    display: 'inline-block',
    transition: 'background 200ms, box-shadow 200ms',
  },
}
